import { LoginCredentials, AuthResponse, User, UserRole } from '../types/index'

/**
 * Mock authentication service for testing purposes
 * Handles dummy users without requiring a backend
 */
export class MockAuthService {
  // Mock users for testing
  private static readonly mockUsers: User[] = [
    {
      id: 'patient-001',
      email: 'patient@gmail.com',
      firstName: 'John',
      lastName: 'Patient',
      role: UserRole.CLIENT,
      avatar: '',
      createdAt: '2024-01-01T00:00:00.000Z',
      updatedAt: '2024-01-01T00:00:00.000Z',
    },
    {
      id: 'therapist-001',
      email: 'therapist@gmail.com',
      firstName: 'Sarah',
      lastName: 'Therapist',
      role: UserRole.THERAPIST,
      avatar: '',
      createdAt: '2024-01-01T00:00:00.000Z',
      updatedAt: '2024-01-01T00:00:00.000Z',
    },
    {
      id: 'admin-001',
      email: 'Admin@gmail.com',
      firstName: 'Sarah',
      lastName: 'Admin',
      role: UserRole.ADMIN,
      avatar: '',
      createdAt: '2024-01-01T00:00:00.000Z',
      updatedAt: '2024-01-01T00:00:00.000Z',
    },
  ]

  private static readonly validPassword = '123456789'

  /**
   * Mock login function
   */
  static async login(credentials: LoginCredentials): Promise<AuthResponse> {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 1000))

    // Find user by email
    const user = this.mockUsers.find(u => u.email === credentials.email)

    if (!user) {
      throw new Error('User not found')
    }

    if (credentials.password !== this.validPassword) {
      throw new Error('Invalid password')
    }

    // Generate mock token
    const token = `mock-token-${user.id}-${Date.now()}`
    const refreshToken = `mock-refresh-${user.id}-${Date.now()}`

    return {
      user,
      token,
      refreshToken,
    }
  }

  /**
   * Mock profile retrieval
   */
  static async getProfile(): Promise<User> {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 500))

    // Get user from localStorage (set during login)
    const userStr = localStorage.getItem('auth-user')
    if (!userStr) {
      throw new Error('No authenticated user')
    }

    try {
      return JSON.parse(userStr)
    } catch {
      throw new Error('Invalid user data')
    }
  }

  /**
   * Mock token validation
   */
  static async validateToken(): Promise<boolean> {
    try {
      const token = localStorage.getItem('auth-token')
      const userStr = localStorage.getItem('auth-user')

      if (!token || !userStr) {
        return false
      }

      // Simple validation - check if token exists and user data is valid
      const user = JSON.parse(userStr)
      return !!(user && (user.id || user._id) && user.email && user.role)
    } catch {
      return false
    }
  }

  /**
   * Mock logout
   */
  static async logout(): Promise<void> {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 300))

    // Clear localStorage
    localStorage.removeItem('auth-token')
    localStorage.removeItem('auth-user')
  }

  /**
   * Get all mock users (for testing purposes)
   */
  static getMockUsers(): User[] {
    return [...this.mockUsers]
  }

  /**
   * Check if email exists in mock users
   */
  static isValidTestEmail(email: string): boolean {
    return this.mockUsers.some(user => user.email === email)
  }

  /**
   * Get user by email
   */
  static getUserByEmail(email: string): User | undefined {
    return this.mockUsers.find(user => user.email === email)
  }
}

export default MockAuthService
